import React, { useState } from "react";
import PageContentContainer from "../../Container/PageContentContainer";
import ActivityOverviewHeader from "../ActivityOverviewHeader/ActivityOverviewHeader";
import UnassignedActivities from "../UnassignedActivities/UnassignedActivities";
import activityStyle from "../ActivityTable/activityStyle";
import { Paper, Stack, TableContainer, Tabs } from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import ActivityTable from "../ActivityTable/ActivityTable";
import PageHeadline from "../../PageHeadline/PageHeadline";
import { userHasOneOfTheseRoles } from "../../../services/backofficeUserService";
import { Roles } from "../../../types/BackofficeUser";
import LawyerAbsenceActivities from "../LawyerAbsenceActivities/LawyerAbsenceActivities";
import TaggedTab from "../../Tabs/TaggedTab";
import PagePagination from "../../PagePagination/PagePagination";
import { useActivityOverviewWithFilters } from "../../../hooks/useActivityOverviewWithFilters";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { useBackofficeUser } from "../../../provider/BackofficeUserProvider";

const ActivityOverview = () => {
  const currentUser = useCurrentUser();
  const { agents } = useBackofficeUser();
  const initialTab = localStorage.getItem("savedTab") || "activities";
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const {
    productFilter,
    setProductFilter,
    activityTypeFilter,
    setActivityTypeFilter,
    timespanFilter,
    setTimespanFilter,
    setSubjectFilter,
    userFilter,
    setUserFilter,
    openOrDoneFilter,
    setOpenOrDoneFilter,
    activitiesResult,
    checkCaseActivitiesResult,
    mailAndDocumentActivitiesResult,
    currentTabTotalPages,
    page,
    setPage,
    updateActivities,
    refreshCounter,
    setRefreshCounter,
    overdueTotal,
    todayTotal,
  } = useActivityOverviewWithFilters({ activeTab, storageKey: "activityFilters" });

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    localStorage.setItem("savedTab", newValue);
  };

  return (
    <PageContentContainer>
      {userHasOneOfTheseRoles(currentUser, [Roles.accountmanager, Roles.admin, Roles.accounting]) && (
        <UnassignedActivities updateFilteredActivities={() => setRefreshCounter(refreshCounter + 1)} />
      )}
      {userHasOneOfTheseRoles(currentUser, [Roles.lawyer]) && (
        <LawyerAbsenceActivities
          refreshCounter={refreshCounter}
          isLoading={activitiesResult.isLoading}
          setSubjectFilter={setSubjectFilter}
          setOpenOrDoneFilter={setOpenOrDoneFilter}
          setProductFilter={setProductFilter}
          setActivityTypeFilter={setActivityTypeFilter}
          setUserFilter={setUserFilter}
          setTimespanFilter={setTimespanFilter}
        />
      )}
      <PageHeadline main="Aktivitätenübersicht" />
      <ActivityOverviewHeader
        overdueTotal={overdueTotal ?? 0}
        todayTotal={todayTotal ?? 0}
        setProductFilter={(data) => {
          updateActivities();
          setProductFilter(data);
        }}
        productFilter={productFilter}
        setActivityTypeFilter={(data: string) => {
          updateActivities();
          setActivityTypeFilter(data);
        }}
        activityTypeFilter={activityTypeFilter}
        userFilter={userFilter}
        setUserFilter={(data) => {
          updateActivities();
          setUserFilter(data);
        }}
        timespanFilter={timespanFilter}
        setTimespanFilter={(data) => {
          updateActivities();
          setTimespanFilter(data);
        }}
        setSubjectFilter={(data) => {
          updateActivities();
          setSubjectFilter(data);
        }}
        agents={agents}
        openOrDoneFilter={openOrDoneFilter}
        setOpenOrDoneFilter={(data) => {
          updateActivities();
          setOpenOrDoneFilter(data);
        }}
      />
      <TableContainer component={Paper}>
        <TabContext value={activeTab}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <TaggedTab label={"Aktivitäten"} value={"activities"} entryCount={activitiesResult.totalItems} />
            <TaggedTab label={"Posteingang"} value={"inbox"} entryCount={mailAndDocumentActivitiesResult.totalItems} />
            <TaggedTab label={"Neue Fälle"} value={"checkCases"} entryCount={checkCaseActivitiesResult.totalItems} />
          </Tabs>
          <TabPanel sx={activityStyle.tabPanel} value="activities">
            {activeTab === "activities" && (
              <>
                <ActivityTable
                  isLoading={activitiesResult.isLoading}
                  activities={activitiesResult.activities ?? []}
                  update={updateActivities}
                  isCompletedView={openOrDoneFilter === "done"}
                />
              </>
            )}
          </TabPanel>
          <TabPanel sx={activityStyle.tabPanel} value="inbox">
            {activeTab === "inbox" && (
              <ActivityTable
                isLoading={mailAndDocumentActivitiesResult.isLoading}
                activities={mailAndDocumentActivitiesResult.activities ?? []}
                update={updateActivities}
                isCompletedView={openOrDoneFilter === "done"}
              />
            )}
          </TabPanel>
          <TabPanel sx={activityStyle.tabPanel} value="checkCases">
            {activeTab === "checkCases" && (
              <ActivityTable
                isLoading={checkCaseActivitiesResult.isLoading}
                activities={checkCaseActivitiesResult.activities ?? []}
                update={updateActivities}
                isCompletedView={openOrDoneFilter === "done"}
              />
            )}
          </TabPanel>
          {!!currentTabTotalPages && currentTabTotalPages > 1 && (
            <Stack direction={"row"} sx={{
              justifyContent: "flex-end"
            }}>
              <PagePagination
                currentPage={page}
                totalPages={currentTabTotalPages}
                handlePrevPage={() => setPage((page) => --page)}
                handleNextPage={() => setPage((page) => ++page)}
              />
            </Stack>
          )}
        </TabContext>
      </TableContainer>
    </PageContentContainer>
  );
};

export default ActivityOverview;
